import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { DateRange, MatCalendar, MatDateRangePicker } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const customPresets: string[] = [
	'rangeDate.TODAY',
	'rangeDate.YESTERDAY',
	'rangeDate.LAST_7_DAYS',
	'rangeDate.LAST_WEEK',
	'rangeDate.THIS_WEEK',
	'rangeDate.LAST_28_DAYS',
	'rangeDate.LAST_30_DAYS',
	'rangeDate.THIS_MONTH',
	'rangeDate.LAST_MONTH',
	'rangeDate.THIS_YEAR',
	'rangeDate.LAST_YEAR'
];

type CustomPreset = (typeof customPresets)[number];

@Component({
	selector: 'app-custom-range-panel',
	templateUrl: './custom-range-panel.component.html',
	styleUrls: ['./custom-range-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomRangePanelComponent<D> implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	readonly customPresets = customPresets;
	@HostBinding('class.touch-ui')
	readonly isTouchUi = this.picker.touchUi;

	selected: Date | null;

	@Input() buttonSelected: CustomPreset | null = 'rangeDate.LAST_30_DAYS';

	constructor(
		private dateAdapter: DateAdapter<D>,
		private picker: MatDateRangePicker<D>,
		private calendar: MatCalendar<D>,
		private cdr: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		const savedPreset = this.readSavedPreset();
		if (savedPreset !== null) {
			this.buttonSelected = savedPreset;
		}

		// Sync once on init (after the datepicker content has fully initialized).
		Promise.resolve().then(() => this.syncPresetSelectionFromCurrentRange());

		// Re-sync whenever the calendar state changes (e.g. user selects dates manually).
		this.calendar.stateChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.syncPresetSelectionFromCurrentRange());
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	selectRange(rangeName: CustomPreset): void {
		this.setPresetSelection(rangeName);
		const [start, end] = this.calculateDateRange(rangeName);

		const startYear = this.dateAdapter.getYear(start);
		const startMonth = this.dateAdapter.getMonth(start);
		const startDay = this.dateAdapter.getDate(start);
		const adapterStart = this.dateAdapter.createDate(startYear, startMonth, startDay);

		const endYear = this.dateAdapter.getYear(end);
		const endMonth = this.dateAdapter.getMonth(end);
		const endDay = this.dateAdapter.getDate(end);
		const adapterEnd = this.dateAdapter.createDate(endYear, endMonth, endDay);

		this.picker.select(adapterStart);
		this.picker.select(adapterEnd);
		this.picker.close();
	}

	private calculateDateRange(rangeName: CustomPreset): [start: D, end: D] {
		const today = this.today;
		const yesterDay = this.dateAdapter.addCalendarDays(today, -1);
		const year = this.dateAdapter.getYear(today);

		switch (rangeName) {
			case 'rangeDate.TODAY':
				return [today, today];
			case 'rangeDate.YESTERDAY':
				return [yesterDay, yesterDay];
			case 'rangeDate.LAST_7_DAYS': {
				const start = this.dateAdapter.addCalendarDays(yesterDay, -6);
				return [start, yesterDay];
			}
			case 'rangeDate.LAST_28_DAYS': {
				const start = this.dateAdapter.addCalendarDays(yesterDay, -27);
				return [start, yesterDay];
			}
			case 'rangeDate.LAST_30_DAYS': {
				const start = this.dateAdapter.addCalendarDays(yesterDay, -29);
				return [start, yesterDay];
			}
			case 'rangeDate.LAST_WEEK': {
				const thisDayLastWeek = this.dateAdapter.addCalendarDays(yesterDay, -7);
				return this.calculateWeek(thisDayLastWeek);
			}
			case 'rangeDate.THIS_WEEK': {
				return this.calculateWeek(today);
			}
			case 'rangeDate.THIS_MONTH': {
				return this.calculateMonth(today);
			}
			case 'rangeDate.LAST_MONTH': {
				const thisDayLastMonth = this.dateAdapter.addCalendarMonths(today, -1);
				return this.calculateMonth(thisDayLastMonth);
			}
			case 'rangeDate.THIS_YEAR': {
				const start = this.dateAdapter.createDate(year, 0, 1);
				const end = this.dateAdapter.createDate(year, 11, 31);
				return [start, end];
			}
			case 'rangeDate.LAST_YEAR': {
				const start = this.dateAdapter.createDate(year - 1, 0, 1);
				const end = this.dateAdapter.createDate(year - 1, 11, 31);
				return [start, end];
			}
			default:
				return [today, today];
		}
	}

	private syncPresetSelectionFromCurrentRange(): void {
		const currentRange = this.getSelectedRangeFromCalendar();
		if (!currentRange) {
			return;
		}

		const matchingPreset = this.findMatchingPreset(currentRange);
		if (matchingPreset) {
			if (this.buttonSelected !== matchingPreset) {
				this.setPresetSelection(matchingPreset);
				this.cdr.markForCheck();
			}
			return;
		}

		if (this.buttonSelected !== null) {
			this.clearPresetSelection();
			this.cdr.markForCheck();
		}
	}

	private getSelectedRangeFromCalendar(): { start: D; end: D } | null {
		const selected = this.calendar.selected;
		if (!selected || typeof selected !== 'object') {
			return null;
		}

		const range = selected as unknown as DateRange<D>;
		if (!('start' in range) || !('end' in range) || range.start == null || range.end == null) {
			return null;
		}

		return { start: range.start, end: range.end };
	}

	private findMatchingPreset(range: { start: D; end: D }): CustomPreset | null {
		for (const preset of customPresets) {
			const [presetStart, presetEnd] = this.calculateDateRange(preset);
			if (this.isSameDay(range.start, presetStart) && this.isSameDay(range.end, presetEnd)) {
				return preset;
			}
		}
		return null;
	}

	private isSameDay(a: D, b: D): boolean {
		return (
			this.dateAdapter.getYear(a) === this.dateAdapter.getYear(b) &&
			this.dateAdapter.getMonth(a) === this.dateAdapter.getMonth(b) &&
			this.dateAdapter.getDate(a) === this.dateAdapter.getDate(b)
		);
	}

	private readSavedPreset(): CustomPreset | null {
		const saved = localStorage.getItem('buttonSelected');
		if (saved && (customPresets as readonly string[]).includes(saved)) {
			return saved as CustomPreset;
		}
		return null;
	}

	private setPresetSelection(preset: CustomPreset): void {
		this.buttonSelected = preset;
		localStorage.setItem('buttonSelected', preset);
	}

	private clearPresetSelection(): void {
		this.buttonSelected = null;
		localStorage.removeItem('buttonSelected');
	}

	private calculateMonth(forDay: D): [start: D, end: D] {
		const year = this.dateAdapter.getYear(forDay);
		const month = this.dateAdapter.getMonth(forDay);
		const start = this.dateAdapter.createDate(year, month, 1);
		const end = this.dateAdapter.addCalendarDays(start, this.dateAdapter.getNumDaysInMonth(forDay) - 1);
		return [start, end];
	}

	private calculateWeek(forDay: D): [start: D, end: D] {
		let deltaStart = this.dateAdapter.getFirstDayOfWeek() - this.dateAdapter.getDayOfWeek(forDay);
		if (deltaStart > 0) {
			deltaStart -= 7;
		}
		const start = this.dateAdapter.addCalendarDays(forDay, deltaStart);
		const end = this.dateAdapter.addCalendarDays(start, 6);
		return [start, end];
	}

	private get today(): D {
		const now = new Date();
		return this.dateAdapter.createDate(now.getFullYear(), now.getMonth(), now.getDate());
	}
}
